/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const SESSION_KEY_TOKEN  = 'authToken';
const SESSION_KEY_ROLE   = 'authRole';
const SESSION_KEY_USERID = 'rilletUserId';

function readSession() {
    return {
        token:  sessionStorage.getItem(SESSION_KEY_TOKEN),
        role:   sessionStorage.getItem(SESSION_KEY_ROLE),
        userId: sessionStorage.getItem(SESSION_KEY_USERID),
    };
}

export function AuthProvider({ children }) {
    const [auth, setAuth] = useState(readSession);

    const login = useCallback((token, role, userId) => {
        sessionStorage.setItem(SESSION_KEY_TOKEN,  token);
        sessionStorage.setItem(SESSION_KEY_ROLE,   role);
        sessionStorage.setItem(SESSION_KEY_USERID, userId);
        // Keep the existing rilletUserId event so other components (NavBar) react
        window.dispatchEvent(new Event('RilletUserIdChange'));
        setAuth({ token, role, userId });
    }, []);

    const logout = useCallback(() => {
        sessionStorage.clear();
        setAuth({ token: null, role: null, userId: null });
    }, []);

    const isAuthenticated = Boolean(auth.token);
    const isAdmin         = auth.role === 'admin';

    return (
        <AuthContext.Provider value={{ ...auth, isAuthenticated, isAdmin, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
